const KELLY_WEAK = 20;
const KELLY_DECENT = 50;
const STOPLOSS_PENALTY_MULTIPLIER = 8; // each stoploss counts as this many bad trades in the Kelly win rate
const GOOD_TRADES_CONFIDENCE = 10; // number of good trades needed for ~75% confidence scaling

const MATURE_15 = 3;
const SUPERMATURE_15 = 8;
const MATURE_120 = 2;
const SUPERMATURE_120 = 4;
const MATURE_1440 = 0;
const SUPERMATURE_1440 = 1;
const MAX_INSPECTED_ITEMS = 50; // to prevent the calculation of "bad" to have the same impact on a huge nr of items as on a small nr
const REALISTIC_GOOD_TRADES_FRACTION = 0.95; // in case we get a better result that 1/20 bad -> assume this will not happen and take 1/20
const CORRECTION_SAFE_GOOD_TRADES = 0.9; // assume that the results may become worse

const getMaturityThresholds = interval => {
	if (interval >= 1440) {
		return { mature: MATURE_1440, supermature: SUPERMATURE_1440 };
	}
	if (interval >= 120) {
		return { mature: MATURE_120, supermature: SUPERMATURE_120 };
	}
	return { mature: MATURE_15, supermature: SUPERMATURE_15 };
};

const _getTradesResultScore = tradesresult => {
	if (!tradesresult) {
		return { score: 1, good: 0, bad: 0, kelly: 0 };
	}

	const tradesResultObject = getTradesResult(tradesresult);
	const good = tradesResultObject.good;
	const bad = tradesResultObject.bad + tradesResultObject.stoploss;
	const kelly = getKellyCriterion(tradesResultObject);

	let score;
	if (kelly <= 0) {
		score = 0; // red - no edge
	} else if (kelly <= KELLY_WEAK) {
		score = 1; // orange - weak edge
	} else if (kelly <= KELLY_DECENT) {
		score = 2; // yellow - decent edge
	} else {
		score = 3; // green - strong edge
	}

	// With a full sample of trades, a weak edge is not good enough -> downgrade to 0
	const finishedTradesCount = tradesresult.filter(t => t.finished).length;
	if (score === 1 && finishedTradesCount >= MAX_INSPECTED_ITEMS) {
		score = 0;
	}

	return { score, good, bad, kelly };
};

const getTradesResult = (tradesresult, minInspectItems = MAX_INSPECTED_ITEMS) => {
	if (!tradesresult) {
		return {
			good: 0,
			bad: 0,
			stoploss: 0,
			trades: 0,
			positiveTradeProfit: 0,
			negativeTradeProfit: 0,
		};
	}

	let good = 0;
	let bad = 0;
	let stoploss = 0;
	let totalPositiveTradeProfit = 0;
	let totalNegativeTradeProfit = 0;

	let counted = 0;
	tradesresult.some(result => {
		// Skip unfinished (active) trades — they have no exit price yet
		if (!result.finished) {
			return false;
		}

		const profitable = result.profitable;
		const exitprice = parseFloat(result.exitprice);
		const entryprice = parseFloat(result.entryprice);

		let profit;
		if (entryprice) {
			if (result.islong) {
				profit = (100 * (exitprice - entryprice)) / entryprice;
			} else {
				profit = (100 * (entryprice - exitprice)) / entryprice;
			}
		} else {
			profit = 0;
		}

		if (profitable) {
			good++;
			totalPositiveTradeProfit += profit;
		} else if (result.signal === 'stoploss') {
			stoploss++;
			totalNegativeTradeProfit += profit;
		} else {
			bad++;
			totalNegativeTradeProfit += profit;
		}
		counted++;
		return counted >= minInspectItems;
	});

	const totalTrades = good + bad + stoploss;

	return {
		good,
		bad,
		stoploss,
		positiveTradeProfit: totalTrades > 0 ? totalPositiveTradeProfit / totalTrades : 0,
		negativeTradeProfit: totalTrades > 0 ? totalNegativeTradeProfit / totalTrades : 0,
		trades: Math.min(minInspectItems, tradesresult.length),
	};
};

const getTradesResultScore = (tradesresult, interval) => {
	const strategyMaturity = getStrategyMaturity(tradesresult, interval);
	const substract = 3 - strategyMaturity;
	let { score } = _getTradesResultScore(tradesresult);

	score = score - substract;

	return Math.max(score, 0);
};

const getStrategyMaturity = (tradesresult, interval) => {
	const { mature, supermature } = getMaturityThresholds(interval);
	if (!tradesresult) {
		return 1;
	}
	// Only count finished trades for maturity — consistent with score calculation
	// which also skips unfinished (active) trades
	const finishedCount = tradesresult.filter(t => t.finished).length;
	if (finishedCount < mature) {
		return 1;
	}
	if (finishedCount < supermature) {
		return 2;
	}
	return 3;
};

const getKellyCriterion = tradesresult => {
	if (!tradesresult) {
		return 0;
	}

	// since tradesresult can come from the strategysimulation as wel as from the true past trades, we need to be able to handle both structures:
	const goodTrades = typeof tradesresult.goodTrades === 'number' ? tradesresult.goodTrades : tradesresult.good;
	const badTrades = typeof tradesresult.badTrades === 'number' ? tradesresult.badTrades : tradesresult.bad;
	const stoplossCount =
		typeof tradesresult.stoplossCount === 'number' ? tradesresult.stoplossCount : tradesresult.stoploss;

	const weightedStoploss = stoplossCount * STOPLOSS_PENALTY_MULTIPLIER;
	const totalTrades = goodTrades + badTrades + weightedStoploss;
	if (totalTrades === 0) {
		return 0;
	}

	let s = Math.min(goodTrades / totalTrades, REALISTIC_GOOD_TRADES_FRACTION); // fraction successrate (stoploss weighted)
	const p = tradesresult.positiveTradeProfit; // profit successful trade in %
	const l = -tradesresult.negativeTradeProfit; // losses wrong trade in %

	if (p === 0) {
		return 0; // cannot divide by zero
	}

	s = CORRECTION_SAFE_GOOD_TRADES * s;

	const kellyCriterion = (s * (p + l) - l) / p;

	// Confidence factor: more good trades = more confidence in the result
	const confidenceFactor = goodTrades / (goodTrades + GOOD_TRADES_CONFIDENCE);

	return Math.max(0, 100 * kellyCriterion * confidenceFactor);
};

const getMaxInspectedItems = () => MAX_INSPECTED_ITEMS;

const getInfo = (tradesresult, interval) => {
	const { score, good, bad, kelly } = _getTradesResultScore(tradesresult);
	const maturity = getStrategyMaturity(tradesresult, interval);
	const { mature, supermature } = getMaturityThresholds(interval);
	const finalScore = Math.max(0, score + maturity - 3);
	const COLORS = {
		0: 'rgb(239, 68, 68)',
		1: 'rgb(217, 119, 6)',
		2: 'rgb(253, 224, 71)',
		3: 'rgb(22, 163, 74)',
	};
	const TEXT_COLORS = {
		0: 'white',
		1: 'white',
		2: 'black',
		3: 'white',
	};
	const COLORS_BETA = {
		1: 'rgb(217, 119, 6)',
		2: 'rgb(253, 224, 71)',
	};
	const TEXT_COLORS_BETA = {
		1: 'white',
		2: 'black',
	};
	const scoreColorStyle = `display: flex; align-items: center; justify-content: center; border-width: 1px; background-color: ${COLORS[finalScore]}; color: ${TEXT_COLORS[finalScore]}; border-radius: 9999px; width: 1.25rem; height: 1.25rem; text-align: center; font-weight: 300; font-size: 0.75rem; line-height: 1.2rem`;

	let beta = '';
	if (maturity < 3) {
		const betaStyle = `display: inline-flex; padding-left: 6px; padding-right: 6px; margin-left: 12px; align-items: center; justify-content: center; border-width: 1px; background-color: ${COLORS_BETA[maturity]}; color: ${TEXT_COLORS_BETA[maturity]}; border-radius: 1rem; rounded-full; height: 1.25rem; text-align: center; font-weight: 300; font-size: 0.75rem; line-height: 1.2rem`;
		beta = `<span style="${betaStyle}">beta</span>`;
	}

	return (
		`<span style="color: yellow; font-size: 120%"><u>Strategy Score</u>: <div style="display: inline-block"><div style="${scoreColorStyle}">${finalScore}</div></div></span><br><br>` +
		'<u>Colors</u><br>' +
		`<div style="display: inline-block; background-color: ${COLORS[0]}; padding: 4px; border-radius: 10%">0: red</div><br>` +
		`<div style="display: inline-block; background-color: ${COLORS[1]}; padding: 4px; border-radius: 10%">1: orange</div><br>` +
		`<div style="display: inline-block; background-color: ${COLORS[2]}; padding: 4px; border-radius: 10%; color: black">2: yellow</div><br>` +
		`<div style="display: inline-block; background-color: ${COLORS[3]}; padding: 4px; border-radius: 10%">3: green</div><br><br>` +
		'<u>Trade Result (Kelly Criterion)</u><br>' +
		`Good trades: ${good}<br>` +
		`Bad trades: ${bad}<br>` +
		`Kelly Criterion: <span style="color: yellow; font-weight: bold">${kelly.toFixed(1)}%</span><br>` +
		`Score: <span style="color: yellow; font-weight: bold">${score}</span> out of 3<br><br>` +
		'<u>Strategy Maturity</u><br>' +
		`Number of trades (finished): ${tradesresult.filter(t => t.finished).length}<br>` +
		`Score: ${maturity} out of 3<br>` +
		`Maturity_Weakness: <span style="color: yellow; font-weight: bold">${3 - maturity}</span>${beta}<br>` +
		`(Maturity_Weakness = 3 - Score)<br><br><br>` +
		'<u>Explanation</u><br><br>' +
		'Strategy Score = Trade_Result - Maturity_Weakness<br>' +
		`=><br>` +
		`<span style="color: yellow; font-weight: bold">${score} - ${3 - maturity} = ${finalScore}</span><br><br>` +
		`Trade result over the last ${MAX_INSPECTED_ITEMS} trades:<br>` +
		`Kelly &lt;= 0 --> score = 0 (no edge)<br>` +
		`Kelly &lt;= ${KELLY_WEAK} --> score = 1 (weak edge)<br>` +
		`Kelly &lt;= ${KELLY_DECENT} --> score = 2 (decent edge)<br>` +
		`Kelly &gt; ${KELLY_DECENT} --> score = 3 (strong edge)<br><br>` +
		`Number of Trades:<br>` +
		`Trades < ${mature} --> score = 1<br>` +
		`Trades < ${supermature} --> score = 2<br>` +
		`Trades >= ${supermature} --> score = 3`
	);
};

module.exports = {
	getTradesResultScore,
	getStrategyMaturity,
	getTradesResult,
	getKellyCriterion,
	getMaxInspectedItems,
	getInfo,
};
